import { BehaviorSubject, Observable, Subject, filter, merge, take } from 'rxjs';
import {
  EventTypes,
  LoginResponse,
  OidcSecurityService,
  PublicEventsService
} from 'angular-auth-oidc-client';

import { AppConstants } from '@app/core/constants/app.constants';
import { Injectable } from '@angular/core';
import { LocalStorageService } from '../storage/storage.service';
import { Logger } from '@app/shared/utils/logger.utils';
import { UserData } from '@app/core/models/userdata.model';

export type AuthResponse = {
  isAuthenticated: boolean;
  userData: UserData;
  accessToken: string;
  idToken: string;
  errorMessage?: string;
};

//TODO: Change the name realtime to someother meaningful name
// We named it as realTIme because at the begining the feature we checked for is called 'RealTime'
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private isAuthenticated$ = new BehaviorSubject(false);
  private userData$ = new BehaviorSubject<UserData>(null!);
  private authToken$ = new BehaviorSubject('');
  private idToken$ = new BehaviorSubject('');
  private authError$ = new BehaviorSubject(false);
  private _currentLocationId$ = new BehaviorSubject<number>(null!);
  private isFeatureRealTime$ = new BehaviorSubject<boolean>(null!);
  private authResult$ = new Subject<AuthResponse>();

  constructor(
    private oidcSecurityService: OidcSecurityService,
    private storageService: LocalStorageService,
    private oidcEventsService: PublicEventsService
  ) {
    const currentLocation = localStorage.getItem(AppConstants.localStorageLocationId);
    if (currentLocation && !isNaN(Number(currentLocation))) {
      this._currentLocationId$.next(Number(currentLocation));
    }

    this.setupEventListener();
  }

  get currentLocationId$() {
    return this._currentLocationId$.asObservable();
  }

  login() {
    this.authError$.next(false);
    this.clearStorage();
    this.oidcSecurityService.authorize();
  }

  logout() {
    this.clearStorage();
    return this.oidcSecurityService.logoffAndRevokeTokens();
  }

  onAuthChange(): Observable<AuthResponse> {
    return merge(this.oidcSecurityService.checkAuth(), this.authResult$.asObservable());
  }

  setSession({
    accessToken,
    idToken,
    isAuthenticated,
    userData,
    errorMessage
  }: Omit<LoginResponse, 'configId'>): { id: number; name: string } {
    this.authToken$.next(accessToken);
    this.idToken$.next(idToken);
    this.isAuthenticated$.next(isAuthenticated);
    this.userData$.next(userData);
    this.authError$.next(!!errorMessage);
    const currentGroupId = this._currentLocationId$.getValue();
    if (!currentGroupId && userData) {
      const defaultGroup = AuthService.getDefaultLocation(userData.groups);
      if (defaultGroup?.id) {
        this._currentLocationId$.next(defaultGroup.id);
        return defaultGroup;
      }
    }

    return {
      id: currentGroupId,
      name:
        userData?.groups?.find((g: { id: number; companyName: string }) => g.id === currentGroupId)
          ?.companyName ?? ''
    };
  }

  static getDefaultLocation(groups: UserData['groups']): { id: number; name: string } | undefined {
    if (groups.length) {
      const location = groups.filter((group) => group.default === true);
      if (location.length) {
        return { id: location[0].id, name: location[0].companyName };
      } else {
        return { id: groups[0].id, name: groups[0].companyName };
      }
    }

    return undefined;
  }

  isAuthenticated() {
    return this.isAuthenticated$.getValue();
  }

  expireSession() {
    this.clearStorage();
    return this.oidcSecurityService.logoffAndRevokeTokens();
  }

  getAuthToken() {
    return this.authToken$.getValue();
  }

  getIdToken() {
    return this.idToken$.getValue();
  }

  getUserData() {
    return this.userData$.getValue();
  }

  getAuthError() {
    return this.authError$.getValue();
  }

  getCurrentLocation() {
    return this._currentLocationId$.getValue();
  }

  setCurrentLocation(value: number) {
    this._currentLocationId$.next(value);
    localStorage.setItem(AppConstants.localStorageLocationId, value.toString());
  }
  getIsFeatureRealTime() {
    return this.isFeatureRealTime$.getValue();
  }

  setIsFeatureRealTime(value: boolean) {
    this.isFeatureRealTime$.next(value);
    localStorage.setItem(AppConstants.localStorageIsFeatureRealtime, value.toString());
  }

  clearStorage() {
    [
      AppConstants.localStorageUserEulaState,
      AppConstants.localStorageIsFeatureRealtime,
      AppConstants.localStorageLocationId,
      AppConstants.localStorageSelectedHuddleFilters,
      AppConstants.localStoragePayerCredentialPageFeature,
      AppConstants.localStorageUserPilotSessionGroup
    ].map((e) => this.storageService.remove(e));
  }

  refreshSession() {
    Logger.trace('[AuthService - refreshSession]');

    this.oidcSecurityService
      .checkAuth()
      .pipe(take(1))
      .subscribe(({ isAuthenticated, userData, accessToken, idToken, errorMessage }) => {
        this.authResult$.next({
          accessToken,
          idToken,
          isAuthenticated,
          userData,
          errorMessage
        });
      });
  }

  private setupEventListener() {
    this.oidcEventsService
      .registerForEvents()
      .pipe(filter((notification) => notification.type === EventTypes.NewAuthenticationResult))
      .subscribe(() => {
        this.refreshSession();
      });
  }

  isEligibilityCompleteFeatureEnabled() {
    const userData = this.userData$.getValue();
    if (!userData?.groups?.length) {
      return false;
    }
    const group = userData.groups.find((obj) => obj.id === this._currentLocationId$.getValue());
    return !!(group && group.features.includes(AppConstants.eligibilityCompleteFeature));
  }
}
